import Head from 'next/head';
import { useRouter } from 'next/router';
import Facebook from './Facebook';
import Twitter from './Twitter';
import { seoSettings } from 'lib/seo/constants';
import RecipeSchema from './RecipeSchema';
import ProductSchema from './ProductSchema';
import HowToSchema from './HowToSchema';
import ArticleSchema from './ArticleSchema';
import FaqSchema from './FaqSchema';
import { sanitizeSeoData } from 'lib/seo/utils';
import { ISeoData, IFavicons } from 'lib/seo/interfaces';
import OrganizationSchema from './OrganizationSchema';
import WebsiteSchema from './WebsiteSchema';

const HOMEPAGE_CANONICAL_PATH_PATTERN = /^\/$|^\/[a-z]{2}-[a-z]{2}\/$/i;

export function normalizeCanonicalUrl(canonicalUrl: string): string {
  if (!canonicalUrl) {
    return canonicalUrl;
  }

  try {
    const parsedUrl = new URL(canonicalUrl);
    if (!HOMEPAGE_CANONICAL_PATH_PATTERN.test(parsedUrl.pathname)) {
      return canonicalUrl;
    }

    const normalizedPath = parsedUrl.pathname === '/' ? '' : parsedUrl.pathname.replace(/\/$/, '');
    return `${parsedUrl.origin}${normalizedPath}${parsedUrl.search}${parsedUrl.hash}`;
  } catch {
    return canonicalUrl;
  }
}

interface ISeoProps {
  seoData: ISeoData;
  logoImgSrc: string;
  logoImgAlt: string;
  favicons: IFavicons;
}

export default function SEO({ seoData, logoImgSrc, logoImgAlt, favicons }: ISeoProps) {
  const router = useRouter();
  const seoDataClone = structuredClone(seoData);

  const cleanSeoData = sanitizeSeoData({
    siteTitle: seoDataClone.siteTitle,
    siteUrl: seoDataClone.siteUrl,
    seoFields: seoDataClone.seoFields,
    fallbackSEOData: seoDataClone.fallbackSEOData,
    recipeData: seoDataClone.recipeData,
    productData: seoDataClone.productData,
    howToData: seoDataClone.howToData,
    articleData: seoDataClone.articleData,
    websiteData: seoDataClone.websiteData,
    organizationData: seoDataClone.organizationData,
  }) as ISeoData;
  const richTextSeoData = sanitizeSeoData(
    {
      faqData: seoDataClone.faqData,
    },
    'richtext'
  ) as Partial<ISeoData>;

  // Data groups
  const contentServiceSeoFields = cleanSeoData.seoFields;
  const pageLevelSeoFields = cleanSeoData.fallbackSEOData;
  const recipeData = cleanSeoData.recipeData;
  const productData = cleanSeoData.productData;
  const howToData = cleanSeoData.howToData;
  const articleData = cleanSeoData.articleData;
  const faqData = richTextSeoData.faqData;
  const websiteData = cleanSeoData.websiteData;
  const organizationData = cleanSeoData.organizationData;

  // Determine whether or not we need to use the Logo image as the fallback image
  const logoImgUrl = seoData.siteUrl + logoImgSrc;
  const fallbackImage = pageLevelSeoFields.image || { sourceUrl: logoImgUrl, altText: logoImgAlt };

  // Base Data fields
  const siteTitle = seoData.siteTitle || pageLevelSeoFields.siteTitle || '',
    pageTitle = contentServiceSeoFields.title || pageLevelSeoFields.title,
    recipeTitleBase = (pageTitle || '')
      .replace(/\bRecipes?\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim(),
    title = recipeData
      ? `${recipeTitleBase || pageTitle} Recipe | ${siteTitle || ''}`
      : `${pageTitle} | ${siteTitle || ''}`, // if seoData isn't set or is an empty string, take from fallback data.
    metaDesc =
      recipeData?.kitchentips ||
      pageLevelSeoFields.metaDescription ||
      contentServiceSeoFields.metaDescription ||
      '',
    canonical = normalizeCanonicalUrl(
      contentServiceSeoFields.canonicalUrl?.replace(/^http:\/\//i, 'https://') ||
        pageLevelSeoFields.canonicalUrl?.replace(/^http:\/\//i, 'https://') ||
        ''
    ),
    robotsNoFollow =
      (contentServiceSeoFields.noFollow ?? pageLevelSeoFields.noFollow)
        ? seoSettings.NOFOLLOW
        : seoSettings.FOLLOW,
    robotsNoIndex = contentServiceSeoFields.noIndex ? seoSettings.NOINDEX : seoSettings.INDEX;

  // Open Graph Data fields
  const ogTitle = contentServiceSeoFields.ogTitle || title,
    ogDesc = contentServiceSeoFields.ogDescription || metaDesc,
    ogUrl =
      contentServiceSeoFields.ogUrl?.replace(/^http:\/\//i, 'https://') ||
      pageLevelSeoFields.ogUrl?.replace(/^http:\/\//i, 'https://') ||
      '',
    ogImageUrl = contentServiceSeoFields.ogImage?.sourceUrl || fallbackImage.sourceUrl || '', // if null, default to fallback image
    ogImageAlt = contentServiceSeoFields.ogImage?.altText || fallbackImage.altText || '',
    ogType = contentServiceSeoFields.ogType ?? 'website', // If type is passed, use that or else it will be website
    ogLocale = router.locale || '',
    ogSiteName = siteTitle;

  // Twitter Data fields
  const twDesc = contentServiceSeoFields.twitterDescription || metaDesc,
    twTitle = contentServiceSeoFields.twitterTitle || title,
    twImageUrl = contentServiceSeoFields.twitterImage?.sourceUrl || fallbackImage.sourceUrl || '', // if null, default to fallback image
    twImageAlt = contentServiceSeoFields.twitterImage?.altText || fallbackImage.altText || '';

  return (
    <>
      <Head>
        {title && <title>{title}</title>}
        {metaDesc && <meta name="description" content={metaDesc} />}
        {canonical && <link rel="canonical" href={canonical} />}
        <meta
          name="robots"
          content={`${robotsNoIndex}, ${robotsNoFollow}, max-image-preview:${seoSettings.MAXIMAGEPREVIEW}, max-snippet:${seoSettings.MAXSNIPPET}, max-video-preview:${seoSettings.MAXVIDEOPREVIEW}`}
        />

        {favicons.icoIconSrc && (
          <link rel="icon" type="image/x-icon" sizes="any" href={favicons.icoIconSrc} />
        )}
        {favicons.svgIconSrc && <link rel="icon" type="image/svg+xml" href={favicons.svgIconSrc} />}
        {favicons.appleIconSrc && (
          <link
            rel="apple-touch-icon"
            type="image/png"
            sizes="180x180"
            href={favicons.appleIconSrc}
          />
        )}
        {favicons.androidIconSrc && (
          <link rel="icon" type="image/png" sizes="192x192" href={favicons.androidIconSrc} />
        )}
      </Head>
      {recipeData && <RecipeSchema {...recipeData} canonical={canonical} />}
      {productData && (
        <ProductSchema
          url={productData.url}
          title={productData.title}
          imageUrl={productData.image?.sourceUrl}
          description={productData.description}
        />
      )}
      {howToData && (
        <HowToSchema
          time={howToData.time}
          supplies={howToData.supplies}
          steps={howToData.steps}
          notes={howToData.notes}
          title={howToData.title}
          imageUrl={howToData.imageUrl}
          description={howToData.description}
          publishedDate={howToData.publishedDate}
          howToVideo={howToData.howToVideo}
          howToVideoDescription={howToData.howToVideoDescription}
          howToVideoPublishedDate={howToData.howToVideoPublishedDate}
        />
      )}
      {articleData && (
        <ArticleSchema
          url={articleData.url}
          title={articleData.title}
          imageUrl={articleData.imageUrl}
          description={articleData.description}
          date={articleData.date}
          modified={articleData.modified}
          copyright={articleData.copyright}
          authorName={articleData.authorName}
        />
      )}
      {faqData && (
        <FaqSchema
          faqCategories={faqData.faqCategories}
          title={faqData.title}
          description={faqData.description}
          publishedDate={faqData.publishedDate}
          authorName={faqData.authorName}
        />
      )}
      {websiteData && <WebsiteSchema name={websiteData.name} url={websiteData.url} />}
      {organizationData && (
        <OrganizationSchema
          name={organizationData.name}
          url={organizationData.url}
          legalName={organizationData.legalName}
          slogan={organizationData.slogan}
          description={organizationData.description}
          logoImgUrl={logoImgUrl}
          alternateNames={organizationData.alternateNames?.map((n) => n.alternateName)}
          telephone={organizationData.telephone}
          address={organizationData.addAddress ? organizationData.address : undefined}
          mainEntityOfPage={organizationData.mainEntityOfPage}
          sameAsSites={organizationData.sameAsSites?.map((s) => s.siteUrl)}
          awards={organizationData.awards?.map((a) => a.award)}
          founders={organizationData.foundingInformation?.founders?.map((f) => f.founderName)}
          foundingDate={organizationData.foundingInformation?.foundingDate}
          socialMediaSites={organizationData.socialMediaSites}
        />
      )}
      <Facebook
        ogTitle={ogTitle}
        ogDesc={ogDesc}
        ogUrl={ogUrl}
        ogImageUrl={ogImageUrl}
        ogImageAlt={ogImageAlt}
        ogType={ogType}
        ogLocale={ogLocale}
        ogSiteName={ogSiteName}
      />
      <Twitter twDesc={twDesc} twTitle={twTitle} twImageUrl={twImageUrl} twImageAlt={twImageAlt} />
    </>
  );
}
